import { http } from "@/shared/lib/api/http"
import { TenantScopeContext } from "@/shared/model/tenant-scope-context"
import {
  LookupMetadataScope,
  type LookupEnvelope,
  type LookupItem,
  type LookupQueryParams,
  type LookupResponse,
  LookupType,
} from "@/shared/model/lookup-types"

type LookupQuery = LookupQueryParams & {
  context?: TenantScopeContext
}

function normalizeLookupEnvelope(value: unknown): LookupResponse {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return { items: [] }
  }

  const record = value as LookupEnvelope
  const data = record.data
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return { items: [] }
  }

  const items = Array.isArray((data as Record<string, unknown>).items)
    ? ((data as Record<string, unknown>).items as LookupItem[])
    : []

  return { items }
}

export async function fetchLookup(type: LookupType, query: LookupQuery = {}): Promise<LookupResponse> {
  const params: Record<string, string | number | null> = {}
  if (typeof query.q === "string" && query.q.trim().length > 0) {
    params.q = query.q.trim()
  }
  if (typeof query.limit === "number") {
    params.limit = query.limit
  }

  // Generic /lookups endpoint returns selectable records by default; only broaden when explicitly asked.
  if (query.includeInactive === true) {
    params.includeInactive = "true"
  }

  if (type === "activities") {
    if (!query.workspaceId) {
      return { items: [] }
    }

    const response = await http.get<unknown>("/finance/activities", {
      params: {
        workspaceId: query.workspaceId,
        // Scope is derived from branchId on the server (parish = system branch id); no scope flag is sent
        // for branch/parish, the backend resolves it from the request branch.
        ...(typeof query.branchId !== "undefined" ? { branchId: query.branchId } : {}),
        // Activities lookup is for picking a live activity — default to active only (exclude
        // completed/cancelled) unless the caller explicitly opts into all statuses.
        activeOnly: !query.includeInactive,
        ...(typeof params.q === "string" ? { search: params.q } : {}),
        ...(typeof params.limit === "number" ? { limit: params.limit } : {}),
        page: 1,
        sortBy: "title",
        sortOrder: "asc",
      },
    })

    const record = (response.data ?? {}) as Record<string, unknown>
    const items = Array.isArray(record.items) ? record.items : []

    return {
      items: items.reduce<LookupItem[]>((acc, item) => {
        const entry = (item ?? {}) as Record<string, unknown>
        const id = typeof entry.id === "string" ? entry.id : ""
        const title = typeof entry.title === "string" ? entry.title : ""
        if (!id || !title) return acc

        acc.push({
          id,
          label: title,
          metadata: {
            branchId: typeof entry.branchId === "string" ? entry.branchId : null,
            incomeCategoryId: typeof entry.incomeCategoryId === "string" ? entry.incomeCategoryId : null,
            scope:
              entry.branchId == null
                ? LookupMetadataScope.Shared
                : query.context === TenantScopeContext.Parish
                  ? LookupMetadataScope.Parish
                  : LookupMetadataScope.Branch,
            status: typeof entry.status === "string" ? entry.status : undefined,
          },
        })

        return acc
      }, []),
    }
  }

  /**
   * Income / expense "categories" are now first-class accounts in the workspace-owned Chart of Accounts
   * (categories->accounts convergence). The old `/finance/income-categories` and `/finance/expense-categories`
   * endpoints were removed with that change, but these lookups kept calling them and simply 404'd — silently,
   * because the caller only ever saw an empty option list.
   *
   * The Chart of Accounts is WORKSPACE-owned, so there is no branch scoping and no parish/branch split to
   * merge: one request, one list, the same for every context.
   */
  if (type === "incomeCategories" || type === "expenseCategories") {
    if (!query.workspaceId) {
      return { items: [] }
    }

    // Only the two params the endpoint declares. Its query schema is a closed `t.Object` with no `search` or
    // `limit`, so passing them would trade the 404 for a 422 — and the chart is a short, workspace-wide list,
    // so searching and capping it here costs nothing.
    const response = await http.get<unknown>("/finance/accounting/accounts", {
      params: {
        workspaceId: query.workspaceId,
        type: type === "incomeCategories" ? "INCOME" : "EXPENSE",
      },
    })

    const record = (response.data ?? {}) as Record<string, unknown>
    const items = Array.isArray(record.items) ? record.items : []
    const search = typeof params.q === "string" ? params.q.trim().toLowerCase() : ""

    const normalizedItems = items.reduce<LookupItem[]>((acc, item) => {
      if (typeof params.limit === "number" && acc.length >= params.limit) return acc
      const entry = (item ?? {}) as Record<string, unknown>
      const id = typeof entry.id === "string" ? entry.id : ""
      const name = typeof entry.name === "string" ? entry.name : ""
      if (!id || !name) return acc

      // The account CODE is what a bookkeeper recognises ("701 Tithes"), so it leads when present — and it is
      // searchable, because someone who knows the code will type the code.
      const code = typeof entry.code === "string" ? entry.code : ""
      // The API already localized a seeded account's name (ADR-0030).
      const displayName = name
      if (search && !`${code} ${name}`.toLowerCase().includes(search)) return acc

      acc.push({
        id,
        label: code ? `${code} ${displayName}` : displayName,
        metadata: {
          // Accounts are workspace-wide: they belong to no branch, and none of them is parish-only.
          branchId: null,
          branchType: null,
          isParishOnly: "false",
        },
      })
      return acc
    }, [])

    return { items: normalizedItems }
  }

  const response = await http.get<LookupEnvelope>(`/lookups/${type}`, { params })
  const normalized = normalizeLookupEnvelope(response.data)
  return { items: normalized.items }
}
